import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Loading, ScalrEmptyBox } from '@/components/customComponents'
import { ScalrBoxAccordionView } from '@/components/customComponents/scalrBox'

import {
  useCategoryAlerts,
  useSalesLensCategories,
  useSalesLensItems,
} from '@/hooks'
import {
  pushNotification,
  selectPageRefreshTrigger,
  useAppDispatch,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { IAbstractLensItem } from '@/types'
import { dateToString } from '@/utils'

const SalesLensPage = () => {
  const navigate = useNavigate()
  const { selectedBranch, date, dateFilterType } =
    useReduxBaseFilter('sales-lens')
  const refreshTrigger = useAppSelector(selectPageRefreshTrigger('sales-lens'))
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const { getCategoryAlertCount, getItemAlertCount } =
    useCategoryAlerts('Sales Lens')

  const {
    formattedCategoriesData,
    loading: categoriesLoading,
    refetch: categoriesRefetch,
  } = useSalesLensCategories(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType,
    selectedBranch
  )

  const {
    fetchItemsForCategory,
    hasItemsForCategory,
    getFormattedItemsForCategory,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useSalesLensItems(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType,
    selectedBranch
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing all sales lens data...')
      setSelectedCategory(null)
      categoriesRefetch()
      itemsRefetch()
    }
  }, [refreshTrigger, categoriesRefetch, itemsRefetch])

  useEffect(() => {
    setSelectedCategory(null)
  }, [selectedBranch, date])

  const displayData = useMemo(() => {
    const result: Array<IAbstractLensItem<any>> = []

    formattedCategoriesData.forEach((category) => {
      result.push({ ...category })

      if (selectedCategory === category.item) {
        const formattedItems = getFormattedItemsForCategory(category.item)
        result.push(...formattedItems)
      }
    })

    return result
  }, [formattedCategoriesData, selectedCategory, getFormattedItemsForCategory])

  const handleCategoryClick = useCallback(
    async (item: IAbstractLensItem<any>) => {
      const categoryName = item.item
      if (!categoryName) return

      // If the item has a parentName, it's an item (child), not a category
      const isActualItem = !!item.parentName

      const isParentCategory =
        formattedCategoriesData.some((cat) => cat.item === categoryName) &&
        !isActualItem

      if (!isParentCategory) {
        return
      }

      if (
        selectedCategory === categoryName &&
        hasItemsForCategory(categoryName)
      ) {
        setSelectedCategory(null)
        return
      }

      try {
        await fetchItemsForCategory(categoryName)
        setSelectedCategory(categoryName)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    },
    [
      selectedCategory,
      formattedCategoriesData,
      fetchItemsForCategory,
      hasItemsForCategory,
    ]
  )

  const handleItemClick = useCallback((item: IAbstractLensItem<any>) => {
    // TODO: check better way to do this
    localStorage.setItem('categoryItem', item.parentName || '')
    navigate(`/clarity-lens/sales-lens/${item.item}`)
  }, [])

  return (
    <div className="space-y-4">
      {categoriesLoading && displayData.length === 0 ? (
        <Loading isContainer />
      ) : displayData.length === 0 ? (
        <ScalrEmptyBox />
      ) : (
        <ScalrBoxAccordionView
          data={displayData}
          onCategoryClick={handleCategoryClick}
          onItemClick={handleItemClick}
          selectedItem={selectedCategory}
          loading={categoriesLoading || itemsLoading}
          getCategoryAlertCount={getCategoryAlertCount}
          getSubItemAlertCount={getItemAlertCount}
        />
      )}
    </div>
  )
}

export default SalesLensPage
